/**
 * License Service - Frontend license resolution and caching
 * Integrates with Injio NG Licensing Functions via backend proxy
 * 
 * LICENSING MODEL FOR CUSTOMER PORTAL (SPA):
 * - Uses TENANT-WIDE licensing (one license per Azure AD tenant/organization)
 * - Client sends: { productId, instanceId: undefined }
 * - Backend extracts tenantId from AAD bearer token automatically
 * - Database query: PartitionKey=<tenant-from-token> AND ProductId=<productId> AND InstanceId IS NULL
 * 
 * This enables standard B2B SaaS licensing where each customer organization gets one license.
 * For per-workspace/department licensing, change instanceId to workspace/department identifier.
 */

import { getCurrentAccount, getLicensingApiToken } from './authService';
import type { TenantLicenseContext, VerifyLicenseRequest, VerifyLicenseResponse } from '@shared/dto/license';

// In-memory license cache keyed by "productId:tenantId"
const licenseCache = new Map<string, TenantLicenseContext>();

// Product ID for the Customer Portal
const PRODUCT_ID = 'injio-intranet-health-portal';

/**
 * Get the current tenant ID from MSAL account
 */
export function getCurrentTenantId(): string | null {
    const account = getCurrentAccount();
    if (!account) {
        return null;
    }

    // MSAL account has tenantId property
    return account.tenantId || null;
}

/**
 * Verify the license for the current tenant
 * Uses in-memory cache and refreshes on expiry
 */
export async function verifyCurrentTenantLicense(): Promise<TenantLicenseContext | null> {
    const tenantId = getCurrentTenantId();

    if (!tenantId) {
        console.warn('[licenseService] Cannot verify license: No tenantId available');
        return null;
    }

    const cacheKey = `${PRODUCT_ID}:${tenantId}`;

    // Check cache first
    const cached = licenseCache.get(cacheKey);
    if (cached && !isLicenseExpiredOrStale(cached)) {
        console.log('[licenseService] Using cached license:', cached);
        return cached;
    }

    console.log('[licenseService] Fetching new license for tenant:', tenantId);

    // Get AAD bearer token for Licensing API
    let bearerToken: string;
    try {
        bearerToken = await getLicensingApiToken();
        console.log('[licenseService] Got bearer token');
    } catch (error) {
        console.error('[licenseService] Failed to get bearer token:', error);
        return createInvalidLicenseContext(tenantId, PRODUCT_ID);
    }

    // Call backend to verify
    try {
        // For SPAs: use null/undefined instanceId for tenant-wide licensing
        // The backend extracts tenantId from AAD bearer token automatically
        // instanceId is only used when you need multiple licenses per tenant (e.g., per-workspace)
        const request: VerifyLicenseRequest = {
            productId: PRODUCT_ID,
            instanceId: undefined, // null/undefined = tenant-wide license (one per Azure AD tenant)
            bearerToken,
        };

        const response = await fetch('/api/verify-license', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
        });

        console.log('[licenseService] API response status:', response.status);

        if (!response.ok) {
            // Treat HTTP errors as invalid license
            console.error('[licenseService] API returned error status:', response.status);
            const errorText = await response.text();
            console.error('[licenseService] Error response:', errorText);
            return createInvalidLicenseContext(tenantId, PRODUCT_ID);
        }

        const licenseResponse: VerifyLicenseResponse = await response.json();
        console.log('[licenseService] License response:', licenseResponse);

        // Check if we got an Invalid status - log why
        if (licenseResponse.status === 'Invalid') {
            console.error('[licenseService] Received Invalid license status');
            console.error('[licenseService] This could mean:');
            console.error('  - No license found for tenant:', tenantId);
            console.error('  - License API is unreachable');
            console.error('  - Network error occurred');
            console.error('  - Bearer token is invalid or expired');
        }

        // Create full context with metadata
        const context: TenantLicenseContext = {
            tenantId,
            productId: PRODUCT_ID,
            status: licenseResponse.status,
            allowedSiteUrls: licenseResponse.allowedSiteUrls,
            features: licenseResponse.features,
            expiresOn: licenseResponse.expiresOn,
            lastVerifiedOn: new Date().toISOString(),
        };

        // Cache the result
        licenseCache.set(cacheKey, context);

        return context;
    } catch (error) {
        // Network errors, timeouts → treat as invalid
        console.error('[licenseService] Error verifying license:', error);
        return createInvalidLicenseContext(tenantId, PRODUCT_ID);
    }
}

/**
 * Check if a site URL is allowed by the current license
 */
export function isSiteUrlAllowed(siteUrl: string, license: TenantLicenseContext | null): boolean {
    if (!license || license.status === 'Invalid') {
        return false;
    }

    if (license.allowedSiteUrls.length === 0) {
        return false;
    }

    // Normalize URLs for comparison
    const normalizedSite = normalizeUrl(siteUrl);
    return license.allowedSiteUrls.some(allowed => normalizeUrl(allowed) === normalizedSite);
}

/**
 * Check if a feature is enabled in the current license
 */
export function isFeatureEnabled(featureId: string, license: TenantLicenseContext | null): boolean {
    if (!license || license.status === 'Invalid') {
        return false;
    }

    return license.features.includes(featureId);
}

/**
 * Clear the license cache (for testing or explicit logout)
 */
export function clearLicenseCache(): void {
    licenseCache.clear();
}

/**
 * Check if cached license is expired or stale
 */
function isLicenseExpiredOrStale(license: TenantLicenseContext): boolean {
    try {
        const expiresOn = new Date(license.expiresOn);
        const now = new Date();

        // If already expired, refresh
        if (expiresOn <= now) {
            return true;
        }

        // Refresh within 5 minutes of expiry (safety margin)
        const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
        if (expiresOn <= fiveMinutesFromNow) {
            return true;
        }

        return false;
    } catch {
        // Invalid date format → treat as stale
        return true;
    }
}

/**
 * Create a default invalid license context
 */
function createInvalidLicenseContext(tenantId: string, productId: string): TenantLicenseContext {
    return {
        tenantId,
        productId,
        status: 'Invalid',
        allowedSiteUrls: [],
        features: [],
        expiresOn: new Date(0).toISOString(),
        lastVerifiedOn: new Date().toISOString(),
    };
}

/**
 * Normalize URL for comparison
 */
function normalizeUrl(url: string): string {
    try {
        const parsed = new URL(url.toLowerCase());
        // Remove trailing slash for consistent comparison
        return parsed.origin + parsed.pathname.replace(/\/$/, '');
    } catch {
        return url.toLowerCase().trim();
    }
}

